import React from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import ComponentSwitch from './ComponentSwitch';
import { Card } from '@/elements/shared/Card/Card';
import Tooltip from '@/ui-kit/components/Tooltip/Tooltip';
import { IUICfgComponentElementOptions, UICfgProductsElementOptionsDefault } from '@canvas-logic/clp-api-types';
import { ComponentElementState } from './ComponentElementState';
import { useComponentProductTypeTranslation } from '@/localization/useProjectLabelTranslation';
import { DisplayContextProvider } from '@canvas-logic/clp-shared-ui';
import { useSlotDisplayContext } from '@/localization/useSlotDisplayContext';
import { gapLevelToCssValue } from '@/elements/shared/gapUtils';

type IProps = {
  state: ComponentElementState;
};

export const ComponentCfgUIElement = observer(function ComponentCfgUIElement({ state }: IProps) {
  const { lookup, productLookup, viewElement } = state;
  const tProductType = useComponentProductTypeTranslation(state.componentKey);
  // The slot context is shared with `<ComponentItemsContainer>` so attributes
  // rendered as siblings to this component see the same `cmpKey` cascade
  // (ADR-0045 gap #2).
  const slotDisplayCtx = useSlotDisplayContext(state.componentKey);

  if (!state.isViewExist) {
    return null;
  }

  const uiSettings = viewElement.uiControl as IUICfgComponentElementOptions;
  const productSettings = uiSettings.products;
  const productsEnabled = productSettings?.enabled ?? UICfgProductsElementOptionsDefault.enabled;

  // CP-1104: under the "2+" rule the component's on/off toggle is hidden for a
  // single-product component (see ComponentSwitch), so ignore the switch there —
  // otherwise the lone product can't be selected/deselected and there's no toggle
  // to enable it. Mirrors ProductsContainer's handling of the same case.
  const showComponent = uiSettings.showComponent ?? '2+';
  const productCount = productLookup.productData?.length ?? 0;
  const switcherHidden = showComponent === '2+' && productCount < 2;
  const showSwitcher = (uiSettings.showSwitcher ?? false) && !switcherHidden;

  const componentIsOff = showSwitcher ? !lookup.current?.value : !productLookup.isEnable();
  const showProducts = productsEnabled && productLookup.initialized && (!componentIsOff || showSwitcher);

  const layout = productSettings?.layout ?? UICfgProductsElementOptionsDefault.layout;
  const showImage = productSettings?.showImage ?? UICfgProductsElementOptionsDefault.showImage;
  const imageFit = productSettings?.imageFit ?? UICfgProductsElementOptionsDefault.imageFit;
  const showTitle = productSettings?.showTitle ?? UICfgProductsElementOptionsDefault.showTitle;
  const showDescription = productSettings?.showDescription ?? UICfgProductsElementOptionsDefault.showDescription;
  const showLongDescription =
    productSettings?.showLongDescription ?? UICfgProductsElementOptionsDefault.showLongDescription;
  const cardOrientation = layout === 'card' ? 'vertical' : 'horizontal';
  // CP-1107: expose the configured "Cards gaps" level as the --cards-gap custom
  // property the .cp-products grid reads (matches ProductsContainer/AttributeLayout).
  const cardsGapCss = gapLevelToCssValue(productSettings?.cardsGap);

  const productDatas = productLookup.productData;
  const canBeDisable = productLookup.canBeDisable;
  const isLocked = !canBeDisable && productDatas.length === 1;
  const isDisabledBySwitch = showSwitcher && componentIsOff;
  const isControlledBySwitch = showSwitcher;

  return (
    <DisplayContextProvider value={slotDisplayCtx}>
      <div data-element-id={viewElement.id} data-component-key={viewElement.source.componentKey}>
        <ComponentSwitch lookup={lookup} viewElement={viewElement} />
        {showProducts && (
          <div
            className={classNames('cp-products', layout === 'card' && 'cp-products--card')}
            style={cardsGapCss ? ({ '--cards-gap': cardsGapCss } as React.CSSProperties) : undefined}
          >
            {productDatas.map((productData) => {
              const selected = String(productData.productId) === productLookup.current?.value.value;
              const hasTooltip = showLongDescription && !!productData.texts.description;

              return (
                <div
                  className={classNames('cp-product')}
                  data-product-id={productData.productId}
                  key={productData.productId}
                >
                  <Tooltip
                    disabled={!hasTooltip}
                    title={''}
                    description={tProductType.productDesc(productData.productKey, productData.texts.description ?? '')}
                  >
                    {(onOpen) => (
                      <Card
                        key={productLookup.id}
                        orientation={cardOrientation}
                        imageSrc={showImage ? productData.images[0]?.url : undefined}
                        imageFit={imageFit}
                        label={
                          showTitle
                            ? tProductType.productName(productData.productKey, productData.texts.name ?? '')
                            : undefined
                        }
                        description={
                          showDescription
                            ? tProductType.productDesc(productData.productKey, productData.texts.shortDescription ?? '')
                            : ''
                        }
                        value={productData.productId}
                        selected={selected}
                        disabled={isLocked || isDisabledBySwitch}
                        hasAlwaysOn={isLocked}
                        onSelect={(value) => {
                          if (isControlledBySwitch && (productDatas.length <= 1 || selected)) return;

                          if (selected) {
                            if (canBeDisable) {
                              productLookup.unselect();
                            }
                          } else {
                            productLookup.select({ value: String(value) });
                          }
                        }}
                        onTooltipClick={onOpen}
                        hasTooltip={hasTooltip}
                      />
                    )}
                  </Tooltip>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DisplayContextProvider>
  );
});
